import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import styled from "styled-components";

import ErrorBanner from "../components/ErrorBanner";
import Img from "../components/Img";
import { useFile } from "../Contexts";
import uploadLogo from "../assets/upload.svg";

import { parseScenariosFromFile } from "../assets/utils/parseScenarios";
// ── Constants ────────────────────────────────────────────────────────────────

const API_BASE = "/api";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(n) {
  if (!n && n !== 0) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(
    Math.floor(Math.log(n) / Math.log(1024)),
    units.length - 1,
  );
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

const fileId = (f) => `${f.name}|${f.size}|${f.lastModified}`;

const toEntry = (file, scenarios = [1]) => ({
  file,
  scenarios,
  previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
});
// ── Styled Components ────────────────────────────────────────────────────────
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  width: 100%;
`;

const EmptyIcon = styled.div`
  font-size: 42px;
  margin-bottom: 12px;
  opacity: 0.4;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 4px;
`;

const EmptySubtitle = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;
const UploadArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  margin: 2rem;
  border: 1px dashed black;
  background-color: #fff;
`;

const DropArea = styled.div`
  background: ${({ $dragging }) => ($dragging ? "#7f8c8d" : "#fff")};
  color: ${({ $dragging }) => ($dragging ? "#122d62" : "#cdcdcd")};
  border-radius: 16px;
  padding: 28px;
  text-align: center;
  cursor: pointer;
  outline: none;
  transition: all 160ms ease;
  user-select: none;
  max-width: 300px;

  .title {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .hint {
    font-size: 0.9rem;
    color: #94a3b8;
  }
`;

const Title = styled.h2``;

const Files = styled.ul`
  margin-top: 16px;
  list-style: none;
  padding: 0;
  display: grid;
  gap: 12px;
`;

const FileItem = styled.li`
  display: grid;
  grid-template-columns: 28px 56px 1fr auto;
  align-items: center;
  gap: 12px;
  background: ${({ $selected }) => ($selected ? "#f0f9ff" : "#fff")};
  border: 1px solid ${({ $selected }) => ($selected ? "#334155" : "#eeeeee")};
  border-radius: 12px;
  padding: 10px 12px;
  cursor: pointer;

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
  }

  img {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 8px;
    background: #fff;
  }

  .meta {
    display: flex;
    flex-direction: column;
  }
  .name {
    color: black;
    font-weight: 600;
  }
  .sub {
    color: #222426;
    font-size: 0.85rem;
  }

  button {
    background: #102a7c;
    color: #e2e8f0;
    padding: 6px 10px;
    border-radius: 8px;
    cursor: pointer;
    &:hover {
      background: #111a2e;
    }
  }
`;

const FilePlaceholder = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 8px;
  background: #fff;
  display: grid;
  place-items: center;
  border: 1px solid gray;
`;

// ── Component ────────────────────────────────────────────────────────────────

export default function DragDropUpload({
  accept = "*",
  multiple = true,
  maxSizeMB = 20,
  onFilesChange,
  onSelectionChange,
}) {
  const {
    setSelectedFile,
    selectedFile,
    files,
    setFiles,
    selectedId,
    setSelectedId,
    setInteractionModel,
  } = useFile();

  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedIds, setUploadedIds] = useState(() => new Set());

  const inputRef = useRef(null);
  const dragCounter = useRef(0);

  const maxSizeBytes = useMemo(() => maxSizeMB * 1024 * 1024, [maxSizeMB]);

  // Deselect if selected file is removed
  useEffect(() => {
    if (!selectedId) return;
    const stillPresent = files.some(({ file }) => fileId(file) === selectedId);
    if (!stillPresent) {
      setSelectedId(null);
      setSelectedFile(null);
      onSelectionChange?.(null);
    }
  }, [files, selectedId, onSelectionChange, setSelectedId, setSelectedFile]);

  // ── Upload ──────────────────────────────────────────────────────────────

  const markUploaded = useCallback((id) => {
    setUploadedIds((prev) => new Set(prev).add(id));
  }, []);

  const uploadFile = useCallback(async (file) => {
    const form = new FormData();
    form.append("file", file);

    const res = await fetch(`${API_BASE}/load-model`, {
      method: "POST",
      body: form,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Upload failed: HTTP ${res.status} - ${text}`);
    }

    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Server error occurred");

    return data.data;
  }, []);

  const ensureUploaded = useCallback(
    async (file) => {
      const id = fileId(file);
      if (uploadedIds.has(id)) return true;

      try {
        const data = await uploadFile(file);
        setInteractionModel(data);
        markUploaded(id);
        return true;
      } catch (err) {
        setError(err.message || "Upload error");
        setFiles((prev) => prev.filter(({ file: f }) => fileId(f) !== id));
        return false;
      }
    },
    [uploadedIds, uploadFile, setInteractionModel, markUploaded, setFiles],
  );

  // ── File list management ────────────────────────────────────────────────

  const addFiles = useCallback(
    async (fileList) => {
      const candidates = Array.from(fileList ?? []).filter(
        (f) => f.size <= maxSizeBytes,
      );
      const succeeded = [];

      for (const file of candidates) {
        const id = fileId(file);
        const alreadyInList = uploadedIds.has(id);

        if (!alreadyInList) {
          try {
            const data = await uploadFile(file);
            setInteractionModel(data);
            markUploaded(id);
            setError(null);
          } catch (err) {
            setError(err.message || "Upload error");
            continue;
          }
        }

        const scenarios = await parseScenariosFromFile(file); // ← add this
        succeeded.push(toEntry(file, scenarios)); // ← pass it in
      }

      if (succeeded.length > 0) {
        setFiles((prev) => {
          const seen = new Set(prev.map(({ file }) => fileId(file)));
          const next = [
            ...prev,
            ...succeeded.filter(({ file }) => !seen.has(fileId(file))),
          ];
          onFilesChange?.(next.map(({ file }) => file));
          return next;
        });
      }

      return succeeded;
    },
    [
      maxSizeBytes,
      uploadedIds,
      uploadFile,
      setInteractionModel,
      markUploaded,
      setFiles,
      onFilesChange,
    ],
  );

  const removeAt = useCallback(
    (idx) => {
      setFiles((prev) => {
        const entry = prev[idx];
        if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);

        const next = prev.filter((_, i) => i !== idx);

        if (fileId(entry.file) === selectedId) {
          setSelectedId(null);
          setSelectedFile(null);
          onSelectionChange?.(null);
        }

        onFilesChange?.(next.map(({ file }) => file));
        return next;
      });
    },
    [
      selectedId,
      setFiles,
      setSelectedId,
      setSelectedFile,
      onSelectionChange,
      onFilesChange,
    ],
  );

  // ── Selection ───────────────────────────────────────────────────────────

  const selectFile = useCallback(
    async (file) => {
      const id = fileId(file);

      if (id === selectedId) {
        setSelectedFile(null);
        setSelectedId(null);
        onSelectionChange?.(null);
        return;
      }

      const ok = await ensureUploaded(file);
      if (ok) {
        setSelectedFile(file);
        setSelectedId(id);
        onSelectionChange?.(file);
      }
    },
    [
      selectedId,
      setSelectedFile,
      setSelectedId,
      onSelectionChange,
      ensureUploaded,
    ],
  );

  // ── Event handlers ──────────────────────────────────────────────────────

  const onPick = useCallback(
    async (e) => {
      if (!e.target.files?.length) return;

      const allowed = Array.from(e.target.files).filter(
        (f) => f.size <= maxSizeBytes,
      );
      const succeeded = await addFiles(allowed);

      if (succeeded.length > 0) {
        const first = succeeded[0].file;
        setSelectedFile(first);
        setSelectedId(fileId(first));
        onSelectionChange?.(first);
      }

      e.target.value = "";
    },
    [maxSizeBytes, addFiles, setSelectedFile, setSelectedId, onSelectionChange],
  );

  const onDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragging(false);
      if (e.dataTransfer?.files?.length) await addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setDragging(true);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (--dragCounter.current <= 0) setDragging(false);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div style={{ width: "100%" }}>
      <UploadArea>
        <DropArea
          role="button"
          tabIndex={0}
          $dragging={dragging}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && inputRef.current?.click()
          }
          onDrop={onDrop}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          aria-label="Upload files by dragging and dropping or browsing"
        >
          <Img src={uploadLogo} width="60px" />
          <div className="title">Drag & drop files here</div>
          <div className="hint">
            or click to browse • Max {maxSizeMB}MB each
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={onPick}
            style={{ display: "none" }}
          />
        </DropArea>
      </UploadArea>

      {error && (
        <ErrorBanner>
          <span className="error-icon">⚠️</span>
          <div className="error-text">{error}</div>
          <button
            className="close-btn"
            onClick={() => setError(null)}
            aria-label="Close error"
          >
            ×
          </button>
        </ErrorBanner>
      )}

      <Title>Import Files</Title>

      {files.length > 0 ? (
        <Files>
          {files.map(({ file, previewUrl }, i) => {
            const id = fileId(file);
            const selected = selectedId === id;

            return (
              <FileItem
                key={`${file.name}-${file.lastModified}-${i}`}
                $selected={selected}
                onClick={async (e) => {
                  if (e.target.tagName.toLowerCase() !== "button")
                    await selectFile(file);
                }}
              >
                <input
                  type="radio"
                  name="fileSelect"
                  aria-label={`Select ${file.name}`}
                  checked={selected}
                  onChange={() => selectFile(file)}
                  onClick={(e) => e.stopPropagation()}
                />

                {previewUrl ? (
                  <img src={previewUrl} alt={file.name} />
                ) : (
                  <FilePlaceholder>📄</FilePlaceholder>
                )}

                <div className="meta">
                  <div className="name">{file.name}</div>
                  <div className="sub">
                    {file.type || "unknown"} • {formatBytes(file.size)}
                  </div>
                </div>

                <button onClick={() => removeAt(i)}>Remove</button>
              </FileItem>
            );
          })}
        </Files>
      ) : (
        <EmptyState>
          <EmptyIcon>📂</EmptyIcon>
          <EmptyTitle>No files uploaded yet</EmptyTitle>
          <EmptySubtitle>Drag & drop or click above to add files</EmptySubtitle>
        </EmptyState>
      )}
    </div>
  );
}
